/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * AI Platform Routes & Providers
 * 
 * Wraps all ai-platform pages with their required providers (MUI Theme, React Query)
 * and defines the AI sub-routes mounted under /ai/* in the main app.
 * 
 * Migrated from the standalone ai-platform project.
 */
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// AI Platform contexts
import { AuthProvider as AIAuthProvider } from './context/AuthContext';
import { ThemeProvider as AIThemeProvider } from './context/ThemeContext';

// Layout
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Pages - eagerly loaded for the most common ones
import AIHubPage from './components/pages/AIHubPage';
import Analytics from './components/pages/Analytics';
import LlmProviderList from './components/llm/LlmProviderList';
import LlmModelList from './components/llm/LlmModelList';
import EmbeddingModelList from './components/llm/EmbeddingModelList';
import AISystemList from './components/system/AISystemList';
import AISystemDetail from './components/system/AISystemDetail';
import ExpertList from './components/expert/ExpertList';
import ExpertDetail from './components/expert/ExpertDetail';
import ScheduleList from './components/schedule/ScheduleList';
import ExecutionDetail from './components/execution/ExecutionDetail';
import SnippetList from './components/snippet/SnippetList';
import SnippetDetail from './components/snippet/SnippetDetail';
import IndicatorSetList from './components/indicatorset/IndicatorSetList';
import IndicatorSetDetail from './components/indicatorset/IndicatorSetDetail';
import AgentList from './components/agent/AgentList';
import AgentDetail from './components/agent/AgentDetail';
import AgentChat from './components/agent/AgentChat';
import GlobalStoragePage from './components/pages/GlobalStoragePage';
import ServiceInstructionsPage from './components/pages/ServiceInstructionsPage';
import ModelLabOverview from './components/model-lab/ModelLabOverview';
import ModelLabDatasetsPage from './components/model-lab/ModelLabDatasetsPage';
import ModelLabFeaturesPage from './components/model-lab/ModelLabFeaturesPage';
import ModelLabRegistryPage from './components/model-lab/ModelLabRegistryPage';
import ModelLabTrainingJobsPage from './components/model-lab/ModelLabTrainingJobsPage';
import ModelLabExperimentsPage from './components/model-lab/ModelLabExperimentsPage';
import ModelLabDeploymentsPage from './components/model-lab/ModelLabDeploymentsPage';
import ModelLabAssetManagerPage from './components/model-lab/ModelLabAssetManagerPage';
import ModelLab3DViewPage from './components/model-lab/ModelLab3DViewPage';
import AccessRouteGate from '../access/AccessRouteGate';
import { AI_ROUTE_SURFACES } from '../access/surfaces';

// React Query client — shared across AI platform pages
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

/**
 * Inner AI routes — rendered inside the MUI + Query providers
 */
function AIRoutesInner() {
  const guard = (surface, element, fallbackTo = '/ai') => (
    <AccessRouteGate surface={surface} fallbackTo={fallbackTo || surface?.fallbackTo}>
      {element}
    </AccessRouteGate>
  );

  return (
    <AIAuthProvider>
      <Routes>
          {/* All routes under /ai/* are protected */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={guard(AI_ROUTE_SURFACES.hub, <AIHubPage />, '/dashboard')} />

            {/* LLM Management */}
            <Route path="providers" element={guard(AI_ROUTE_SURFACES.providers, <LlmProviderList />)} />
            <Route path="models" element={guard(AI_ROUTE_SURFACES.models, <LlmModelList />)} />
            <Route path="embedding-models" element={guard(AI_ROUTE_SURFACES.embeddingModels, <EmbeddingModelList />)} />

            {/* AI Systems */}
            <Route path="systems" element={guard(AI_ROUTE_SURFACES.systems, <AISystemList />)} />
            <Route path="systems/:id" element={guard(AI_ROUTE_SURFACES.systemDetail, <AISystemDetail />)} />

            {/* Model Lab */}
            <Route path="ml" element={guard(AI_ROUTE_SURFACES.mlOverview, <ModelLabOverview />)} />
            <Route path="ml/datasets" element={guard(AI_ROUTE_SURFACES.mlDatasets, <ModelLabDatasetsPage />)} />
            <Route path="ml/features" element={guard(AI_ROUTE_SURFACES.mlFeatures, <ModelLabFeaturesPage />)} />
            <Route path="ml/models" element={guard(AI_ROUTE_SURFACES.mlModels, <ModelLabRegistryPage />)} />
            <Route path="ml/models/:assetId/manager" element={guard(AI_ROUTE_SURFACES.mlModelManager, <ModelLabAssetManagerPage />)} />
            <Route path="ml/models/:assetId/3d" element={guard(AI_ROUTE_SURFACES.mlModel3d, <ModelLab3DViewPage />)} />
            <Route path="ml/training-jobs" element={guard(AI_ROUTE_SURFACES.mlTrainingJobs, <ModelLabTrainingJobsPage />)} />
            <Route path="ml/experiments" element={guard(AI_ROUTE_SURFACES.mlExperiments, <ModelLabExperimentsPage />)} />
            <Route path="ml/deployments" element={guard(AI_ROUTE_SURFACES.mlDeployments, <ModelLabDeploymentsPage />)} />

            {/* Experts */}
            <Route path="experts" element={guard(AI_ROUTE_SURFACES.experts, <ExpertList />)} />
            <Route path="experts/:id" element={guard(AI_ROUTE_SURFACES.expertDetail, <ExpertDetail />)} />

            {/* Snippets */}
            <Route path="snippets" element={guard(AI_ROUTE_SURFACES.snippets, <SnippetList />)} />
            <Route path="snippets/:id" element={guard(AI_ROUTE_SURFACES.snippetDetail, <SnippetDetail />)} />

            {/* Indicator Sets */}
            <Route path="indicator-sets" element={guard(AI_ROUTE_SURFACES.indicatorSets, <IndicatorSetList />)} />
            <Route path="indicator-sets/:id" element={guard(AI_ROUTE_SURFACES.indicatorSetDetail, <IndicatorSetDetail />)} />

            {/* Agents */}
            <Route path="agents" element={guard(AI_ROUTE_SURFACES.agents, <AgentList />)} />
            <Route path="agents/:id" element={guard(AI_ROUTE_SURFACES.agentDetail, <AgentDetail />)} />
            <Route path="agents/:id/chat" element={guard(AI_ROUTE_SURFACES.agentChat, <AgentChat />)} />

            {/* Schedules & Executions */}
            <Route path="schedules" element={guard(AI_ROUTE_SURFACES.schedules, <ScheduleList />)} />
            <Route path="executions" element={guard(AI_ROUTE_SURFACES.analytics, <Navigate to="/ai/analytics?tab=runs" replace />)} />
            <Route path="executions/legacy" element={guard(AI_ROUTE_SURFACES.analytics, <Navigate to="/ai/analytics?tab=runs&view=legacy" replace />)} />
            <Route path="executions/:id" element={guard(AI_ROUTE_SURFACES.executionDetail, <ExecutionDetail />)} />

            {/* Analytics & Billing */}
            <Route path="analytics" element={guard(AI_ROUTE_SURFACES.analytics, <Analytics />)} />
            <Route path="billing" element={<Navigate to="/billing" replace />} />

            {/* Global Storage */}
            <Route path="global-storage" element={guard(AI_ROUTE_SURFACES.globalStorage, <GlobalStoragePage />)} />

            {/* Service Instructions */}
            <Route path="service-instructions" element={guard(AI_ROUTE_SURFACES.serviceInstructions, <ServiceInstructionsPage />)} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/ai" replace />} />
          </Route>
        </Routes>

        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
    </AIAuthProvider>
  );
}

/**
 * Top-level AI Platform wrapper — provides MUI theme + React Query
 * Mount this under <Route path="/ai/*" element={<AIRoutes />} />
 */
export default function AIRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <AIThemeProvider>
        <AIRoutesInner />
      </AIThemeProvider>
    </QueryClientProvider>
  );
}
